import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { datasetGroupService, datasetService, fieldService, referenceService } from '../services';
import {
  AttributeSensitivity,
  DatasetSubtypeOperation,
  formatData,
  formatConnectionString,
  getHierarchyPath,
  getObjectNameFromSelection,
  handleError,
  fetchDatasetFields,
  searchDatasetFields,
  withFieldPaths,
  IsNullableValues,
  Role,
  Asset,
  sourceLinkAction,
} from '../helpers';
import {
  Changelog,
  DataTable,
  MainColumn,
  ObjectDetail,
  ObjectDetails,
  RelationshipGraph,
  Tabs,
  textareaToPlainText,
  PrincipalLink,
} from '../components';
import {
  useCustomProperties,
  useSideDialog,
  useModalDialog,
  useQueryParams,
  useGlobalState,
  usePrincipal,
  useNotFound,
  useMessage,
  useStatus,
} from '../hooks';

/**
 * The datasource type category the kind filter is offered for. A BI platform is the only kind of
 * source that puts genuinely different objects side by side in one container — a workspace holds
 * semantic models, reports and dashboards at once — which is what makes filtering by kind useful.
 * A database schema's tables and views, or an API's endpoints, do not need it.
 */
const KIND_FILTER_CATEGORY = 'bi_platform';

export const DatasetGroup = ({ abortFetch }) => {
  const { queryParams, setHistory, resetParams } = useQueryParams();
  const [datasetGroup, setDatasetGroup] = useState([]);
  const [datasets, setDatasets] = useState();
  // The fields of the datasets that have been expanded, held apart from the datasets so that
  // reloading them — which every save does — does not empty the open rows, and so that the
  // Relationships tab keeps being given datasets alone.
  const [fields, setFields] = useState([]);
  const [fromReferences, setFromReferences] = useState([]);
  const [loggedInUser] = useGlobalState('loggedInUser');
  const [statuses] = useGlobalState('statuses');
  const [settings] = useGlobalState('settings');
  const [selectedItems, setSelectedItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingTable, setLoadingTable] = useState(true);

  const [defaultSelectedTab, setDefaultSelectedTab] = useState({ tab: queryParams.tab });

  const clearSelectedItems = () => {
    setSelectedItems([]);
  };

  const { system_id, datasource_id, dataset_group_id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const { showMessage } = useMessage();
  const { showSideDialog, hideSideDialog, setSideDialog } = useSideDialog(clearSelectedItems);
  const { showModalDialog, hideModalDialog } = useModalDialog();
  const { fetchPrincipals } = usePrincipal();
  const { notFound, setNotFound, NotFound } = useNotFound();
  const { renderCustomPropertyValues, fetchCustomProperties } = useCustomProperties({
    objectName: Asset.DatasetGroup,
    objectIds: [dataset_group_id],
  });
  const { appendStatus } = useStatus({ setState: setDatasets, assetCategory: Asset.System });

  useEffect(() => {
    fetchItems();
    fetchDatasets();
  }, [dataset_group_id]);

  const fetchItems = async () => {
    datasetGroupService
      .getById(dataset_group_id, abortFetch)
      .then(res => {
        if (res.dataset_groups.length < 1) {
          setNotFound(true);
        } else {
          setDatasetGroup(res.dataset_groups[0]);
          fetchFromReferences(dataset_group_id);
        }
      })
      .catch(err => {
        handleError({ err, showMessage, setNotFound });
      })
      .finally(() => setLoading(false));
  };

  const fetchDatasets = async () => {
    const promise = new Promise(resolve => {
      datasetService
        .getByDatasetGroupId(dataset_group_id, abortFetch)
        .then(res => {
          const allDatasets = res?.datasets || [];
          const datasets =
            res &&
            allDatasets.map(x => {
              return {
                ...x,
                // The tree holds datasets and fields together, so a row is identified by its kind
                // and its id rather than by an id that only means something within one table.
                unique_id: `${Asset.Dataset}-${x.dataset_id}`,
                parent_unique_id: x.parent_dataset_id
                  ? `${Asset.Dataset}-${x.parent_dataset_id}`
                  : null,
                object_id: x.dataset_id,
                object_name: Asset.Dataset,
                link: `/systems/${system_id}/${datasource_id}/${dataset_group_id}/${x.dataset_id}`,
                pii: x.is_pii ? 'PII' : null,
                pii_tooltip: x.is_pii
                  ? 'Contains Fields classified as Personally Identifiable Information (PII)'
                  : null,
                connector_filter_name:
                  x.connector_name && x.secondary_connector_name
                    ? 'both'
                    : x.connector_name || x.secondary_connector_name || 'none',
                // API method pill is shown inline before the name for top-level datasets only.
                dataset_subtype_operation_top:
                  x.parent_dataset_id == null ? x.dataset_subtype_operation : undefined,
                dataset_subtype_tooltip: x?.dataset_subtype_code?.toUpperCase() + ' HTTP method',
                // Child datasets show their dataset_type icon inline; top-level shows the pill instead.
                dataset_type_child: x.parent_dataset_id == null ? undefined : x.dataset_type_name,
                // The type icon shown in the name cell outside API datasources. A property of its
                // own so it marks datasets only — a field carries its dataset's type as well.
                dataset_type_icon: x.dataset_type_name,
                className: 'bold',
                dataset_path: getHierarchyPath(x, allDatasets, Asset.Dataset, ' > '),
              };
            });

          setDatasets(datasets);
          appendStatus();
          setLoadingTable(false);
          fetchPrincipals(datasets, setDatasets);
          resolve(datasets);
        })
        .catch(err => {
          handleError({ err, showMessage }, () => {
            setLoading(false);
          });
        });
    });

    Promise.all([promise, fetchFieldStatistics()])
      .then(result => {
        updateFieldStatistics(result[0], result[1]);
      })
      .catch(err => {
        handleError({ err, showMessage });
      });
  };

  const fetchFromReferences = async datasetGroupId => {
    referenceService
      .getByFromId(Asset.DatasetGroup, datasetGroupId || dataset_group_id, abortFetch)
      .then(res => {
        res && setFromReferences(res.references);
      })
      .catch(err => {
        handleError({ err, showMessage });
      });
  };

  const fetchFieldStatistics = async () => {
    return new Promise(resolve => {
      datasetService
        .getFieldStatistics(dataset_group_id, abortFetch)
        .then(res => {
          resolve(res.field_statistics);
        })
        .catch(() => {
          return null;
        });
    });
  };

  const updateFieldStatistics = (data, statistics) => {
    let statistic = {};
    let maxFieldCount = 0;
    let maxRowCount = 0;
    let maxDiskSize = 0;

    statistics.forEach(row => {
      if (row.field_count > maxFieldCount) {
        maxFieldCount = row.field_count;
      }
    });

    data.forEach(row => {
      if (parseInt(row.dataset_row_count) > maxRowCount) {
        maxRowCount = parseInt(row.dataset_row_count);
      }
      if (parseInt(row.dataset_size_bytes) > maxDiskSize) {
        maxDiskSize = parseInt(row.dataset_size_bytes);
      }
    });

    setDatasets(prev =>
      prev.map(item => {
        statistic = statistics.find(x => x.dataset_id === item.dataset_id);
        // Statistics are rolled up over the dataset subtree on the backend, so a parent dataset's
        // field count, documentation level, sensitivity and PII reflect all its nested children.
        // Override the row's own-attribute sensitivity/PII (from v_dataset) with the rolled-up values.
        const isPii = statistic ? statistic.is_pii : item.is_pii;
        return {
          ...item,
          field_count: statistic ? statistic.field_count : 0,
          attribute_ratio: statistic ? statistic.attribute_ratio : 0,
          field_count_max: maxFieldCount,
          // How many field rows appear directly under this dataset when it is opened, as opposed to
          // the rolled-up field_count above.
          own_field_count: statistic ? statistic.own_field_count : 0,
          attribute_sensitivity_level: statistic
            ? statistic.attribute_sensitivity_level
            : item.attribute_sensitivity_level,
          attribute_sensitivity_name: statistic
            ? statistic.attribute_sensitivity_name
            : item.attribute_sensitivity_name,
          is_pii: isPii,
          pii: isPii ? 'PII' : null,
          pii_tooltip: isPii
            ? 'Contains Fields classified as Personally Identifiable Information (PII)'
            : null,
          row_count: item.dataset_row_count ? parseInt(item.dataset_row_count) : null,
          row_count_max: maxRowCount,
          disk_size: item.dataset_size_bytes ? parseInt(item.dataset_size_bytes) : null,
          disk_size_max: maxDiskSize,
        };
      })
    );
  };

  /**
   * Replaces the loaded fields of the given datasets, leaving every other dataset's alone.
   * @param {Array<number|string>|number|string} datasetIds - The datasets just read
   * @param {object[]} rows - Their fields as just read, in one list
   */
  const mergeFields = (datasetIds, rows) => {
    // A set of what is being replaced, so re-reading many datasets is one pass over the state and
    // one render rather than one of each per dataset.
    const ids = new Set([].concat(datasetIds).map(Number));

    setFields(prev => [...prev.filter(field => !ids.has(Number(field.dataset_id))), ...rows]);
    appendStatus({ setState: setFields, assetCategory: Asset.System });
  };

  /**
   * Loads the fields of one dataset, for expanding its row. The promise is returned so the table can
   * show the row as loading until they arrive.
   *
   * A field's own children come with them, so opening a nested field costs no further request.
   *
   * @param {object} item - The row being expanded
   * @returns {Promise<void>|undefined} undefined when there is nothing to load for this row
   */
  const fetchFields = item => {
    if (item.object_name !== Asset.Dataset) return;

    // Already read once. The rows are still here — collapsing a row does not discard them — so
    // re-expanding shows them again without another request. Anything changed from this page re-reads
    // them (refreshFields), which is what keeps them current.
    if (fields.some(field => Number(field.dataset_id) === Number(item.dataset_id))) return;

    return fetchDatasetFields({ datasetIds: [item.dataset_id], signal: abortFetch })
      .then(rows => mergeFields(item.dataset_id, withFieldPaths(rows, datasets)))
      .catch(err => {
        handleError({ err, showMessage });
      });
  };

  /**
   * Re-reads the fields of the datasets whose rows are open, after one of them has changed. The
   * datasets themselves are reloaded separately, which is what refreshes the counts.
   *
   * All of them are read in one request: opening a row keeps its fields until the page is left, so
   * what this covers grows as the tree is explored, and a request each would be a request per row
   * ever opened.
   *
   * @param {Array<number|string>} [datasetIds] - Datasets to re-read; defaults to every open one
   */
  const refreshFields = datasetIds => {
    const ids = datasetIds || [...new Set(fields.map(field => field.dataset_id))];

    if (ids.length === 0) return Promise.resolve();

    return fetchDatasetFields({ datasetIds: ids })
      .then(rows => mergeFields(ids, withFieldPaths(rows, datasets)))
      .catch(err => {
        handleError({ err, showMessage });
      });
  };

  /**
   * Searches the group's fields server-side, for the table's filter box.
   * @param {string} term - What the user typed
   * @param {{signal: AbortSignal, top: number, fields: string[]}} options - From the DataTable
   * @returns {Promise<object[]>} Matching field rows
   */
  const searchFields = (term, { signal, top, fields: ticked }) =>
    searchDatasetFields({
      scope: `dataset_group_id eq ${Number(dataset_group_id)}`,
      term,
      fields: ticked,
      top,
      signal,
    }).catch(err => {
      handleError({ err, showMessage });
      return [];
    });

  /**
   * Opens the right dialog for the selected rows, dispatching on what kind of asset they are. A
   * dataset and a field are different resources with different forms, and the toolbar acts on
   * whichever is selected.
   * @param {'add'|'add_child'|'edit'|'delete'} method
   * @param {object[]} [items] - Rows to act on; defaults to the current selection
   */
  const handleAsset = (method, items) => {
    items = items || selectedItems;
    const objectName = getObjectNameFromSelection(items, Asset.Dataset);

    if (objectName === Asset.Field) {
      switch (method) {
        case 'add':
          // The selection is a field, so a new one is added to the same dataset. Only the dataset is
          // carried over — the form must not copy the selected field's own properties.
          showSideDialog(
            'addField',
            [{ dataset_id: items[0].dataset_id, status_id: statuses.default_system_status_id }],
            addField
          );
          break;
        case 'add_child':
          showSideDialog('addChildField', items, addField);
          break;
        case 'edit':
          showSideDialog('editField', items, editField);
          break;
        case 'delete':
          showModalDialog('deleteField', items, deleteField);
          break;
        default:
        // nothing
      }
      return;
    }

    if (objectName === 'mixed') return;

    switch (method) {
      case 'add':
        showSideDialog('addDataset', items, addDataset);
        break;
      case 'add_child':
        showSideDialog('addChildDataset', items, addDataset);
        break;
      case 'add_field':
        // Adding a field to the selected dataset: the dataset is the parent, not something to copy.
        showSideDialog(
          'addField',
          [{ dataset_id: items[0].dataset_id, status_id: statuses.default_system_status_id }],
          addField
        );
        break;
      case 'edit':
        showSideDialog('editDataset', items, editDataset);
        break;
      case 'delete':
        showModalDialog('deleteDataset', items, deleteDataset);
        break;
      default:
      // nothing
    }
  };

  /**
   * Creates one or more fields and refreshes the dataset they belong to.
   * @param {object|object[]} data - The field(s) to create
   */
  const addField = async data => {
    const rows = Array.isArray(data) ? data : [data];

    await fieldService
      .create({ fields: rows })
      .then(res => {
        setSideDialog(prev => ({ ...prev, formErrors: {} }));
        // The datasets carry the field counts, so they are reloaded alongside the fields themselves.
        fetchDatasets();
        refreshFields([...new Set(rows.map(row => row.dataset_id))]);
        showMessage(res.message);
      })
      .catch(err => {
        handleError({ err, showMessage, setSideDialog }, () => {
          throw err;
        });
      });
  };

  /**
   * Updates one or more fields.
   * @param {object|object[]} data - The field(s) to update
   */
  const editField = async data => {
    const rows = Array.isArray(data) ? data : [data];

    await fieldService
      .update({ fields: rows })
      .then(async res => {
        // An edit can move a field to another dataset, so both the dataset it left and the one it
        // arrived at are re-read — which is every open dataset, plus any newly named one.
        refreshFields([
          ...new Set([
            ...fields.map(field => field.dataset_id),
            ...rows.map(row => row.dataset_id),
          ]),
        ]);
        fetchDatasets();
        showMessage(res.message);
        clearSelectedItems();
        hideSideDialog();
      })
      .catch(err => {
        handleError({ err, showMessage, setSideDialog }, () => {
          throw err;
        });
      });
  };

  /**
   * Deletes one or more fields. The confirmation offers to delete their child fields with them, so
   * it answers with the rows and that choice rather than with the rows alone.
   * @param {{items: object|object[], deleteChildren: boolean}} data
   */
  const deleteField = async data => {
    const items = Array.isArray(data.items) ? data.items : [data.items];

    await fieldService
      .destroy({ fields: items, deleteChildren: data.deleteChildren })
      .then(async res => {
        fetchDatasets();
        refreshFields();
        showMessage(res.message);
        hideModalDialog();
        clearSelectedItems();
      })
      .catch(err => {
        handleError({ err, showMessage }, () => {});
      });
  };

  const addDataset = async data => {
    const datasets = { datasets: Array.isArray(data) ? data : [data] };

    await datasetService
      .create(datasets)
      .then(res => {
        setSideDialog(prev => ({ ...prev, formErrors: {} }));
        fetchDatasets();
        showMessage(res.message);
      })
      .catch(err => {
        handleError({ err, showMessage, setSideDialog }, () => {
          throw err;
        });
      });
  };

  const editDatasetGroup = async data => {
    const datasetGroups = { dataset_groups: Array.isArray(data) ? data : [data] };

    await datasetGroupService
      .update(datasetGroups)
      .then(async res => {
        fetchItems();
        fetchCustomProperties(false);
        showMessage(res.message);
        hideSideDialog();
      })
      .catch(err => {
        handleError({ err, showMessage, setSideDialog }, () => {
          throw err;
        });
      });
  };

  const editDataset = async data => {
    const datasets = { datasets: Array.isArray(data) ? data : [data] };

    await datasetService
      .update(datasets)
      .then(async res => {
        fetchDatasets();
        showMessage(res.message);
        clearSelectedItems();
        hideSideDialog();
      })
      .catch(err => {
        handleError({ err, showMessage, setSideDialog }, () => {
          throw err;
        });
      });
  };

  const deleteDatasetGroup = async data => {
    await datasetGroupService
      .destroy({ dataset_groups: data })
      .then(async res => {
        showMessage(res.message);
        hideModalDialog();

        if (location.key !== 'default') {
          // Arrived here by navigating inside Katalogue, so return the user where they came from.
          navigate(-1);
        } else {
          // Landed directly on this page — a deep link, a refresh or a new tab — so there is no
          // history to return to. Fall back to the deleted asset's parent.
          navigate(`/systems/${system_id}/${datasource_id}`);
        }
      })
      .catch(err => {
        handleError({ err, showMessage }, () => {});
      });
  };

  const deleteDataset = async data => {
    await datasetService
      .destroy({ datasets: data })
      .then(async res => {
        fetchDatasets();
        showMessage(res.message);
        hideModalDialog();
        clearSelectedItems();
      })
      .catch(err => {
        handleError({ err, showMessage }, () => {});
      });
  };

  const onTabChange = tab => {
    resetParams({ tab: tab });
  };

  // The tree: the group's datasets, plus the fields of the datasets that have been opened. The fields
  // are rows of the same table, placed under their dataset — or under their parent field — which is
  // what keeps their checkbox and their columns in line with the rows above them.
  const assetRows = useMemo(() => [...(datasets || []), ...fields], [datasets, fields]);

  /**
   * Whether to offer the kind filter over the datasets table. Only for a BI platform: it is the one
   * kind of source that puts genuinely different objects side by side in one container, a workspace
   * holding semantic models, reports and dashboards at once. The table itself decides whether there
   * is more than one kind to choose between.
   */
  const showKindFilter = datasetGroup?.datasource_type_category === KIND_FILTER_CATEGORY;

  /**
   * Remembers the selected kind in ?object=, which is this page's to own — the table keeps the
   * selection but cannot know which parameter a page keeps it in.
   * @param {string} kind - A dataset_type_code, or the table's "all"
   * @returns {void}
   */
  const onKindChange = kind => {
    setHistory({ object: kind });
  };

  // Datasets and fields are separate resources with separate forms, so Edit and Delete act on one
  // kind at a time.
  const enableEdit =
    selectedItems.length === 0 ||
    selectedItems.every(item => item.object_name === selectedItems[0].object_name);

  const mixedSelectionText =
    'Assets of different types have been selected. Make sure to only select assets of the same type.';

  /**
   * Builds the "Add Child" button for the selected row: a child dataset under a dataset that can
   * have them, a field under a dataset, or a nested field under a field that can hold them (the
   * object or array fields of an API response).
   * @returns {object} A DataTable button definition, or {} when the selection cannot have children
   */
  const getAddChildButton = () => {
    if (selectedItems.length !== 1) return {};

    const item = selectedItems[0];

    if (item.object_name === Asset.Field) {
      return {
        label: 'Add Child',
        disabled: !item.datatype_has_children,
        tooltip: item.datatype_has_children
          ? 'Add a new field with the currently selected field as parent'
          : 'Only a field whose datatype can hold other fields can have child fields',
        onClick: items => handleAsset('add_child', items),
      };
    }

    if (item.dataset_type_has_children) {
      return {
        label: 'Add Child',
        tooltip: 'Add a new dataset with the currently selected dataset as parent',
        onClick: items => handleAsset('add_child', items),
      };
    }

    // A dataset that cannot hold datasets can still hold fields, which is the only child it has.
    return {
      label: 'Add Child',
      tooltip: 'Add a new field to the currently selected dataset',
      onClick: items => handleAsset('add_field', items),
    };
  };

  const data = loading ? [] : datasetGroup;

  const editable =
    loggedInUser &&
    (loggedInUser.user_role_name === Role.admin || loggedInUser.user_role_name === Role.editor);

  if (notFound) {
    return <NotFound />;
  }

  return (
    <div className="columns narrow-margin">
      <MainColumn>
        <ObjectDetails
          type="Dataset Group"
          title={data.dataset_group_name}
          statusId={data.status_id}
          subtitleText={
            data &&
            !loading &&
            formatConnectionString(
              data.datasource_database,
              data.datasource_hostname,
              [data.dataset_group_name],
              data.datasource_type_code,
              data.datasource_type_category
            )
          }
          description={data.dataset_group_description}
          fromReferences={fromReferences}
          loading={loading}
          details={[
            {
              title: 'Source Description',
              data: textareaToPlainText(data.dataset_group_source_description),
            },
            { title: 'Source Id', data: data.dataset_group_source_id },
            { title: 'Source URL', data: data.dataset_group_source_url, isUrl: true },
            {
              title: 'Source Last Modified',
              data: formatData(data.dataset_group_source_modified, 'datetime'),
            },
            {
              title: 'Source Created',
              data: formatData(data.dataset_group_source_created, 'datetime'),
            },
            ...renderCustomPropertyValues().details,
            {
              title: 'Id',
              data: data.dataset_group_id,
              tooltip: 'Katalogue-internal id for this asset',
            },
          ]}
          openLink={sourceLinkAction(
            data.dataset_group_source_url,
            data.connector_code,
            data.connector_name
          )}
          editable={editable}
          buttons={[
            {
              buttonStyle: 'main-button',
              value: 'Edit',
              onClick: () => showSideDialog('editDatasetGroup', [data], editDatasetGroup),
            },
            {
              value: 'Delete',
              onClick: () => showModalDialog('deleteDatasetGroup', [data], deleteDatasetGroup),
            },
          ]}
        >
          <ObjectDetail label="Owner" loading={loading}>
            <PrincipalLink principalId={data.owner_principal_id} loading={loading} />
          </ObjectDetail>

          {renderCustomPropertyValues().main}
        </ObjectDetails>
      </MainColumn>

      <div className="column">
        <Tabs
          className="slim left"
          onTabChange={onTabChange}
          defaultSelectedTab={defaultSelectedTab}
          disableTabsWithoutResults={true}
        >
          <div
            label="Datasets"
            tabid="datasets"
            resultCount={
              // Count only top-level datasets — nested children (e.g. an API endpoint's
              // request/response/parameter datasets) are shown as expandable rows, not tallied here.
              loadingTable || datasets.length === 0
                ? undefined
                : datasets.filter(x => x.parent_dataset_id == null).length
            }
          >
            <DataTable
              columns={[
                {
                  id: 'dataset_name',
                  name: 'Name',
                  link: ':link',
                  tooltip: 'dataset_description',
                  tooltipSubtitle: 'dataset_path',
                  // Per row: a dataset's name is the heading of what sits under it, a field's name
                  // is not. The glossary tree marks its entities the same way.
                  className: ':className',
                  inline: [
                    // For API datasources: top-level endpoints show the method as a pill, while
                    // child datasets (request body / response / parameters) show their type icon.
                    ...(datasetGroup.datasource_type_code === 'api'
                      ? [
                          {
                            type: 'pill',
                            value: 'dataset_subtype_operation_top',
                            label: 'dataset_subtype_name',
                            values: DatasetSubtypeOperation,
                            tooltip: 'dataset_subtype_tooltip',
                            className: 'width-50',
                          },
                          {
                            type: 'icon',
                            icon: 'dataset_type_child',
                            tooltip: 'dataset_type_name',
                            // Mutually exclusive with the method pill per row — share the pill's
                            // single header spacer/sort control instead of adding a second one.
                            sortable: false,
                          },
                        ]
                      : // Everywhere else the dataset's type icon rides in the name cell rather than
                        // in a column of its own, so the expand control sits next to the checkbox
                        // instead of behind an icon. Its own property, since a field row carries the
                        // type of the dataset it belongs to and must not draw it as if it were its own.
                        [
                          { type: 'icon', icon: 'dataset_type_icon', tooltip: 'dataset_type_name' },
                        ]),
                    // What a field is, for the field rows nested under a dataset — the same things
                    // its own list shows beside its name, and in the same order: what kind of thing
                    // it is before the name, what it holds after it. None of them belongs to a
                    // dataset, so they claim no header slot: sorting the tree on a property only some
                    // of its rows have would shuffle the rest for no reason.
                    { type: 'icon', icon: 'key_type', tooltip: 'key_list', sortable: false },
                    {
                      type: 'icon',
                      icon: 'datatype_category',
                      tooltip: 'datatype_fullname',
                      sortable: false,
                    },
                    {
                      /*
                      type: 'text',
                      value: 'datatype_fullname',
                      tooltip: 'datatype_fullname',
                      sortable: false,
                      after: true,
                    */
                    },
                    {
                      type: 'pill',
                      value: 'is_nullable',
                      label: 'not_null',
                      values: IsNullableValues,
                      sortable: false,
                      after: true,
                    },
                  ],
                },
                {
                  id: 'dataset_description',
                  name: 'Description',
                  type: 'description',
                  viewMode: 'descriptions',
                },
                {
                  id: 'field_count',
                  name: 'Fields',
                  type: 'chart',
                  // A field has no fields of its own, so the cell is left blank rather than reading
                  // "-", which would say the number is unknown.
                  appliesTo: Asset.Dataset,
                  chartType: 'bar',
                  data: { data: 'field_count', max: 'field_count_max' },
                  viewMode: 'statistics',
                },
                ...(datasetGroup.datasource_type_code !== 'api'
                  ? [
                      {
                        id: 'row_count',
                        name: 'Rows',
                        type: 'chart',
                        appliesTo: Asset.Dataset,
                        chartType: 'bar',
                        data: { data: 'row_count', max: 'row_count_max' },
                        viewMode: 'statistics',
                      },
                    ]
                  : []),
                {
                  id: 'attribute_ratio',
                  appliesTo: Asset.Dataset,
                  name: 'Documentation Level',
                  type: 'chart',
                  chartType: 'percent-bar',
                  data: { data: 'attribute_ratio' },
                  viewMode: 'statistics',
                },
                {
                  id: 'pii',
                  name: '',
                  type: 'icon',
                  tooltip: 'pii_tooltip',
                  className: 'no-right-margin',
                },
                {
                  id: 'attribute_sensitivity_name',
                  name: 'Data Sensitivity',
                  type: 'pill',
                  value: 'attribute_sensitivity_level',
                  values: AttributeSensitivity,
                  tooltip: 'attribute_sensitivity_name',
                },
                ...(settings.SHOW_SYSTEM_STATUSES
                  ? [
                      {
                        id: 'status_name',
                        name: 'Status',
                        type: 'pill',
                        value: 'status_id',
                        values: statuses,
                        tooltip: 'status_description',
                      },
                    ]
                  : []),
                {
                  id: 'principal_name',
                  name: 'Owner',
                  type: 'owner',
                  // An owner belongs to the asset itself: a field inherits its parent's, and showing it
                  // again on every row would read as the field having an owner of its own.
                  appliesTo: Asset.Dataset,
                  link: '/:principal_url',
                  data: { photo: 'principal_photo' },
                  tooltipSubtitle: 'principal_type_name',
                },
              ]}
              buttons={[
                {
                  label: '+ Add',
                  action: 'add',
                  mainButton: true,
                  onClick: items => handleAsset('add', items),
                },
                getAddChildButton(),
                {
                  label: 'Edit',
                  action: 'edit',
                  disabled: !enableEdit,
                  tooltip: !enableEdit && mixedSelectionText,
                  onClick: items => handleAsset('edit', items),
                },
                {
                  label: 'Delete',
                  action: 'delete',
                  disabled: !enableEdit,
                  tooltip: !enableEdit && mixedSelectionText,
                  onClick: items => handleAsset('delete', items),
                },
              ]}
              data={assetRows}
              idColumn="unique_id"
              primaryColumn="dataset_name"
              parentIdColumn="parent_unique_id"
              tabFilter={
                showKindFilter
                  ? {
                      name: 'dataset_kind',
                      // The category rather than the type: a paginated report is a report and both
                      // kinds of app are apps, and the types say so themselves.
                      column: 'dataset_type_category',
                      // The order the lookup defines the kinds in, which for Power BI is most
                      // substantial first.
                      sortColumn: 'dataset_type_id',
                      tooltip: 'Show only assets of one kind',
                      defaultValue: queryParams.object,
                      onChange: onKindChange,
                    }
                  : undefined
              }
              expandable={true}
              selectedItems={selectedItems}
              setSelectedItems={setSelectedItems}
              loading={loadingTable}
              editable={editable}
              filterable={true}
              filterObjects={[
                'dataset_name',
                'dataset_description',
                'dataset_type_name',
                'is_pii',
                'attribute_sensitivity_name',
                ...(settings.SHOW_SYSTEM_STATUSES ? ['status_name'] : []),
                'principal_name',
                'connector_filter_name',
                // A field's datatype. Only fields have one, so it is ticked under its own name rather
                // than sharing a column with the datasets' type.
                'datatype_name',
              ]}
              showViewModes={true}
              // A dataset's fields are read when it is opened, so the count of the fields it holds
              // directly is what tells the table it has any before they exist.
              childCountColumn="own_field_count"
              onExpand={fetchFields}
              childFilter={{ fetch: searchFields }}
            />
          </div>

          <div label="Relationships" tabid="relationships">
            <RelationshipGraph
              datasets={datasets}
              objectName={Asset.DatasetGroup}
              objectId={dataset_group_id}
              rollupToParent
            />
          </div>

          <div label="Changelog" tabid="changelog">
            <Changelog
              objectName={Asset.DatasetGroup}
              objectId={dataset_group_id}
              filterObjects={[
                'changedData.new_data||old_data.dataset_group_name',
                'changedData.new_data||old_data.dataset_group_description',
                'changedData.new_data||old_data.datasource_name',
                'changedData.new_data||old_data.datasource_type_name',
                'changedData.new_data||old_data.principal_name',
                'changedData.property',
                'changed_by_user_username',
                'is_service_account_user',
              ]}
              filterObjectsValueMap={{
                is_service_account_user: {
                  true: { label: 'Datasource Sync' },
                  false: { label: '' },
                },
              }}
            />
          </div>
        </Tabs>
      </div>
    </div>
  );
};
